/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import {
  ActionMenu,
  ActionMenuProps,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_TRANSPARENT,
  BUTTON_THEME_SUCCESS,
  Breadcrumbs,
  BreadcrumbsProps,
  Button,
  ButtonList,
  ButtonListProps,
  DIALOG_SIZE_SM,
  DialogContentProps,
  DialogProps,
  DropdownItemData,
  ICON_ADD,
  SPINNER_TYPE_REGULAR,
  Spinner,
  TOAST_TYPE_DANGER,
  Tab,
  Tabs,
  Toast,
} from '@squiz/sds';
import { DxpScreen } from 'apps/console-core/src/Components/DxpScreen/DxpScreen';
import { useCapabilityBreadcrumbs } from 'apps/console-core/src/Hooks/UseCapabilityBreadcrumbs/UseCapabilityBreadcrumbs';
import { getCapabilityPath } from 'apps/console-core/src/Utilities/GetCapabilityPath/GetCapabilityPath';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useDialogStore, useToastStore } from 'dxpConsoleCore-mf/State';
import difference from 'lodash/difference';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isUndefined from 'lodash/isUndefined';
import pick from 'lodash/pick';
import property from 'lodash/property';
import some from 'lodash/some';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CDP_STRINGS } from '../../Constants/Strings.constants';
import { FormFieldValidationResponse } from '../../Hooks/UseFormValidation/UseFormValidation.hook';
import {
  EntitySegment,
  EntitySegmentGroup,
  EntitySegmentList,
  useEvents,
  useSegments,
} from '../../Services/segmentation';
import { useSegmentForm } from '../../Services/segmentation/src/Hooks/UseSegmentForm/UseSegmentForm.hook';
import { useSegmentGroups } from '../../Services/segmentation/src/Hooks/UseSegmentGroups/UseSegmentGroups.hook';
import { useScvSchema } from '../../Services/single-customer-view';
import { SEGMENTATION_DATA_IDS } from '../Segmentation/Segmentation.constants';
import { DeleteSegmentDialog } from '../Segmentation/elements/SegmentsList/elements/DeleteSegmentDialog/DeleteSegmentDialog';
import { DeleteSegmentDialogProps } from '../Segmentation/elements/SegmentsList/elements/DeleteSegmentDialog/DeleteSegmentDialog.props';
import { SEGMENT_BUILDER_DATA_IDS, defaultSegmentDataFromAPI } from './SegmentBuilder.constants';
import { DiscardChanges } from './elements/DiscardChanges/DiscardChanges';
import { SegmentGroupSwitcher } from './elements/SegmentGroupSwitcher/SegmentGroupSwitcher';
import {
  SegmentGroupSwitcherOperator,
  SegmentPriorityItem,
} from './elements/SegmentGroupSwitcher/SegmentGroupSwitcher.types';
import { SegmentBuilderForm } from './forms/SegmentBuilderForm/SegmentBuilderForm';
import { formatFromAPI } from './utils/FormatFromAPI/FormatFromAPI';

let segmentCounter = 1;

/**
 * The create and update segment screen.
 * @export
 * @returns {JSX.Element} The `<SegmentBuilder />` component.
 */
export const SegmentBuilder = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { segmentId } = useParams();
  const [multiple, setMultiple] = useState<boolean>(false);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [groupOperator, setGroupOperator] = useState<SegmentGroupSwitcherOperator | null>(null);
  const [groupPriorities, setGroupPriorities] = useState<Array<SegmentPriorityItem>>([]);
  const { updateActiveToast } = useToastStore();
  const [cacheKey, setCacheKey] = useState<string>(``);
  const groups = useSegmentGroups(cacheKey);
  const segments = useSegments(cacheKey);
  const schema = useScvSchema();
  const events = useEvents(cacheKey);
  const { addSegments, groupId, isSaving, removeSegment, setSegments, saveSegments, segmentChanges, updateSegment } =
    useSegmentForm({
      groups,
      segmentId,
    });
  const existingSegment = useMemo((): EntitySegment | undefined => {
    return find(segments, (segment): boolean => segment.internalId === segmentId);
  }, [segmentId, segments]);
  const { updateActiveDialog } = useDialogStore();

  const [allValidationMessages, setAllValidationMessages] = useState<Record<string, string | null>>({});
  const [currentValidationMessages, setCurrentValidationMessages] = useState<Record<string, string | null>>({});

  /**
   * Handle updates to a single form including segment data and form validation response.
   */
  const handleFormUpdate = useCallback(
    (
      changedSegment: EntitySegment,
      formResponse: FormFieldValidationResponse,
      existingMessages: Record<string, string | null>,
    ): void => {
      const nextMessages = {
        ...existingMessages,
        ...formResponse.messages,
      };
      setAllValidationMessages(nextMessages);
      setCurrentValidationMessages(pick(nextMessages, Object.keys(currentValidationMessages)));
      updateSegment(changedSegment);

      const updatedPriorityItem = {
        id: `${changedSegment.internalId}`,
        name: /^\s*$/.test(changedSegment.name)
          ? `Segment ${changedSegment.internalId?.replace(`tmp-`, ``)}`
          : changedSegment.name,
      };

      setGroupPriorities((oldPriorities) => {
        let foundExistingPriorityItem = false;
        const nextPriorities = oldPriorities.map((item) => {
          if (item.id === changedSegment.internalId) {
            foundExistingPriorityItem = true;
            return updatedPriorityItem;
          }
          return item;
        });

        if (!foundExistingPriorityItem) {
          return [...nextPriorities, updatedPriorityItem];
        }

        return nextPriorities;
      });
    },
    [currentValidationMessages, updateSegment],
  );

  /**
   * Tab list data (when segments are part of a group).
   */
  const tabs: Array<Tab> = useMemo((): Array<Tab> => {
    return isArray(segmentChanges)
      ? segmentChanges.map((segment, index): Tab => {
          const { internalId } = segment;
          return {
            content: (
              <div id={`segment-${internalId}`} key={`segment-${internalId}`}>
                <SegmentBuilderForm
                  data={formatFromAPI(segment)}
                  errors={currentValidationMessages}
                  events={events}
                  groupCount={segmentChanges.length}
                  internalId={segment.internalId}
                  onChange={(changedSegment, responses): void => {
                    handleFormUpdate(changedSegment, responses, allValidationMessages);
                  }}
                  onDelete={(deletedSegmentId, messages) => {
                    // Clears validation messages.
                    const nextMessages = {
                      ...allValidationMessages,
                      ...messages,
                    };
                    setAllValidationMessages(nextMessages);
                    setCurrentValidationMessages(pick(nextMessages, Object.keys(currentValidationMessages)));
                    removeSegment(deletedSegmentId);

                    // Remove the segment from the group priorities list, if it exists.
                    setGroupPriorities((oldPriorities) => {
                      const nextPriorities = oldPriorities.filter((item) => {
                        return item.id !== deletedSegmentId;
                      });
                      return nextPriorities;
                    });
                  }}
                  schema={schema}
                />
              </div>
            ),
            key: `${segment.internalId}`,
            label: segment.name === `` ? `Segment ${segment.internalId?.replace(`tmp-`, ``)}` : segment.name,
          };
        })
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValidationMessages, segmentChanges]);

  const getSegmentForGroup = useCallback(
    (gid: string): Array<EntitySegment> => {
      if (!groups) {
        return [];
      }
      return groups[gid].segments.map((segmentId): EntitySegment => {
        const segment = find(segments, (item) => `${item?.internalId}` === segmentId) as EntitySegment;
        return segment;
      });
    },
    [groups, segments],
  );

  // Labels
  const labels = useMemo(() => {
    return {
      actionMenuAriaLabel: t(CDP_STRINGS.segmentActionMenuAriaLabel),
      addSegment: t(CDP_STRINGS.addSegment),
      createSegmentText: t(CDP_STRINGS.segmentBuilderCreateButton),
      deleteSegment: t(CDP_STRINGS.segmentActionMenuDelete),
      deleteSegmentDialogHeading: t(CDP_STRINGS.deleteSegmentDialogHeading),
      errorRequiredField: t(CDP_STRINGS.errorRequiredField),
      errorToastRequiredFields: t(CDP_STRINGS.errorToastRequiredFields),
      errorToastUnableToSave: t(CDP_STRINGS.errorToastUnableToSave),
      heading: t(CDP_STRINGS.segmentBuilder),
      saveSegmentText: t(CDP_STRINGS.save),
      segmentation: t(CDP_STRINGS.segmentation),
      toastErrorMessage: t(CDP_STRINGS.toastErrorLabel),
      toastSuccessCreateMessage: t(CDP_STRINGS.toastSuccessCreateMessage),
      toastSuccessCreateMessageEnd: t(CDP_STRINGS.toastSuccessCreateMessageEnd),
      toastSuccessCreateTitle: t(CDP_STRINGS.toastSuccessCreateLabel),
      toastSuccessUpdateMessage: t(CDP_STRINGS.toastSuccessUpdateLabel),
    };
  }, [t]);

  // Set initial state.
  useEffect(() => {
    const selectedSegment = find(segments, (segment) => property(`internalId`)(segment) === segmentId);
    // Only trigger if there are no changes (i.e. initial state).
    if (!segmentChanges) {
      if (groupId && groups && segments) {
        const groupSegments = segmentId ? getSegmentForGroup(groupId) : [];
        setMultiple(true);
        // @ts-expect-error ignored for now.
        setSegments((currentSegments): EntitySegmentList => {
          if (currentSegments === null || currentSegments.length !== groupSegments.length) {
            return groupSegments;
          }
          return currentSegments;
        });
        segmentCounter = groupSegments.length;
        setAllValidationMessages({});
      } else if (segmentId && selectedSegment) {
        setSegments([selectedSegment as EntitySegment]);
        setAllValidationMessages({});
      }
    }

    if (groupId && groups && !groupName && segmentChanges) {
      const group = property(groupId)(groups) as EntitySegmentGroup;

      if (group) {
        const groupSegments = getSegmentForGroup(groupId);
        setGroupName(group.name);
        setGroupOperator(group.operator as SegmentGroupSwitcherOperator);
        const groupSegmentIds = groupSegments
          .filter((item) => !isUndefined(item))
          .map((segment) => segment.internalId) as Array<string>;

        if (!group.priority) {
          group.priority = [];
        }

        // Group segments added onto the end of the existing priority list to ensure the saved
        // selected priority order is preserved.
        const priorities = group.priority
          .concat(difference(group.priority, groupSegmentIds))
          .map((internalId) => {
            return groupSegments.filter((segment) => segment.internalId === internalId).shift();
          })
          .filter((item) => !isUndefined(item))
          .map((item) => {
            return {
              id: item.internalId,
              name: item.name,
            };
          }) as Array<SegmentPriorityItem>;

        setGroupPriorities(priorities);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, groups, segmentChanges, segmentId, segments]);

  const pageHeading = useMemo((): string => {
    if (multiple && groupName !== '') {
      return groupName as string;
    }

    if (segmentId && existingSegment?.name) {
      return existingSegment?.name as string;
    }

    return labels.heading;
  }, [existingSegment?.name, groupName, labels.heading, multiple, segmentId]);

  // Breadcrumbs
  const breadcrumbProps: BreadcrumbsProps = {
    currentPageName: pageHeading,
    links: [...useCapabilityBreadcrumbs(), { name: labels.segmentation, url: getCapabilityPath(`/segmentation`) }],
  };
  const breadcrumbs = <Breadcrumbs {...breadcrumbProps} data-id="segment-builder-breadcrumbs" />;

  /**
   * Handle save click events.
   */
  const saveHandler = useCallback((): void => {
    if (groupName !== null && /^\s*$/.test(groupName)) {
      allValidationMessages['group-name'] = labels.errorRequiredField;
    }

    // Update current validation messages to all validation messages.
    setCurrentValidationMessages(allValidationMessages);
    // If there are any unresolved messages (i.e. not null) validation has failed.
    const unresolvedMessages: boolean = some(Object.entries(allValidationMessages), ([, message]) => {
      return isString(message);
    });
    if (unresolvedMessages) {
      updateActiveToast(
        <Toast
          showToast
          stateHandler={useToastStore}
          title={labels.errorToastUnableToSave}
          toastContent={<div>{labels.errorToastRequiredFields}</div>}
          toastType={TOAST_TYPE_DANGER}
        />,
      );
    } else {
      const groupDataToSave = {
        name: groupName ? `${groupName}` : undefined,
        operator: groupOperator || SegmentGroupSwitcherOperator.ByRecency,
        priority: groupPriorities.map((priority) => {
          return priority.id;
        }),
      };

      // eslint-disable-next-line promise/catch-or-return, promise/always-return
      saveSegments(groupDataToSave).then((): void => {
        navigate({ pathname: getCapabilityPath(`/segmentation`) });
      });
    }
  }, [
    allValidationMessages,
    groupName,
    groupOperator,
    groupPriorities,
    labels.errorRequiredField,
    labels.errorToastRequiredFields,
    labels.errorToastUnableToSave,
    navigate,
    saveSegments,
    updateActiveToast,
  ]);

  const builderFormButtonListProps = useMemo((): ButtonListProps => {
    const buttonKeys = [];
    const buttons = [];

    // Conditionally add a cancel button.
    if (!segmentId) {
      buttonKeys.push(`cancel-changes`);
      buttons.push(<DiscardChanges key="discard-changes" />);
    }

    buttonKeys.push(`save-changes`);
    buttons.push(
      <Button
        data-id={SEGMENT_BUILDER_DATA_IDS.buttonSaveSegment}
        disabled={isSaving}
        isLoading={isSaving}
        key="save-changes"
        label={isUndefined(segmentId) ? labels.createSegmentText : labels.saveSegmentText}
        onClick={saveHandler}
        text={isUndefined(segmentId) ? labels.createSegmentText : labels.saveSegmentText}
        theme={BUTTON_THEME_SUCCESS}
      />,
    );

    return {
      buttonKeys,
      buttons,
    };
  }, [isSaving, labels.createSegmentText, labels.saveSegmentText, saveHandler, segmentId]);

  const menuItems: Array<DropdownItemData> = useMemo((): Array<DropdownItemData> => {
    const items = [];
    const canDeleteSegment = hasPermission(PERMISSIONS.dxpSegmentation.SEGMENT_DELETE);

    if (existingSegment) {
      const dialogProps: DialogProps = {
        dialogContent: {
          renderContent: (props: DialogContentProps): JSX.Element => {
            const dialogProps: DeleteSegmentDialogProps = {
              ...props,
              clearCache: setCacheKey,
              id: multiple ? (groupId as string) : (existingSegment.internalId as string),
              isGroup: multiple,
              name: multiple ? (groupName as string) : (existingSegment.name as string),
              onAfterSave: (): void => {
                navigate({ pathname: getCapabilityPath(`/segmentation`) });
              },
            };

            return <DeleteSegmentDialog {...dialogProps} />;
          },
        },
        dialogSize: DIALOG_SIZE_SM,
        heading: labels.deleteSegmentDialogHeading,
        preventFullScreen: true,
        scrollOnOverflow: false,
        stateHandler: useDialogStore,
      };

      if (canDeleteSegment) {
        // Delete segment menu item
        items.push({
          action: (): void => {
            updateActiveDialog(dialogProps);
          },
          className: `segment-builder__action-menu-delete`,
          dataID: SEGMENTATION_DATA_IDS.segmentActionMenuItemDelete,
          key: `delete`,
          label: labels.deleteSegment,
        });
      }
    }
    return items;
  }, [
    existingSegment,
    groupId,
    groupName,
    labels.deleteSegment,
    labels.deleteSegmentDialogHeading,
    multiple,
    navigate,
    updateActiveDialog,
  ]);

  const actionMenuProps: ActionMenuProps = {
    buttonFormat: BUTTON_FORMAT_SOFT,
    buttonLabel: 'Action menu',
    itemList: menuItems,
    sectionKey: `segment-builder-action-menu`,
  };

  // Loading state.
  if (!groups || !segments || !schema || !events) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  return (
    <DxpScreen
      breadcrumbs={breadcrumbs}
      buttons={[<ActionMenu key="action-menu" {...actionMenuProps} />]}
      heading={pageHeading}
      showSeparator
    >
      <div className="segment-builder-wrapper">
        <SegmentGroupSwitcher
          disabled={isString(segmentId)}
          errors={allValidationMessages}
          groupName={groupName}
          multiple={multiple}
          onNameChange={(name): void => {
            if (/^\s*$/.test(name)) {
              setAllValidationMessages({
                ...allValidationMessages,
                [`group-name`]: labels.errorRequiredField,
              });
              setCurrentValidationMessages({
                ...currentValidationMessages,
                [`group-name`]: labels.errorRequiredField,
              });
            } else {
              setAllValidationMessages({
                ...allValidationMessages,
                [`group-name`]: null,
              });
              setCurrentValidationMessages({
                ...currentValidationMessages,
                [`group-name`]: null,
              });
            }
            setGroupName(name);
          }}
          onOperatorChange={(option) => {
            setGroupOperator(option);
          }}
          onSegmentPriorityChange={setGroupPriorities}
          onSwitch={(multiSelectValue): void => {
            setMultiple(multiSelectValue);
            if (multiSelectValue && !segmentId) {
              // Set defaults for multi-mode.
              setSegments([
                { ...JSON.parse(defaultSegmentDataFromAPI), code: ``, internalId: `tmp-1`, name: `` },
                { ...JSON.parse(defaultSegmentDataFromAPI), code: ``, internalId: `tmp-2`, name: `` },
              ]);
              // Set some default messages that will cause initial validation to fail until values are entered.
              setAllValidationMessages({
                'conditions-tmp-1': '',
                'conditions-tmp-2': '',
                'group-name': '',
                'id-tmp-1': '',
                'id-tmp-2': '',
                'name-tmp-1': '',
                'name-tmp-2': '',
              });
              // Set an empty group name.
              setGroupName(``);
              segmentCounter = 2;
            } else {
              setGroupName(null);
              setSegments(null);

              setCurrentValidationMessages({});
              setAllValidationMessages({
                ...allValidationMessages,
                'conditions-tmp-2': null,
                'group-name': null,
                'id-tmp-2': null,
                'name-tmp-2': null,
              });
            }
          }}
          operator={groupOperator || SegmentGroupSwitcherOperator.ByRecency}
          segmentPriority={groupPriorities}
        />
        {multiple && tabs.length >= 1 && (
          <Tabs
            id="segments-list-tabs"
            listLabel="Segments"
            tabAction={
              <Button
                format={BUTTON_FORMAT_TRANSPARENT}
                icon={ICON_ADD}
                onClick={(): void => {
                  segmentCounter += 1;
                  addSegments([
                    {
                      ...JSON.parse(defaultSegmentDataFromAPI),
                      code: ``,
                      internalId: `tmp-${segmentCounter}`,
                      name: ``,
                    },
                  ]);
                  // Default validation messages ensures a click on the save/create button
                  // will be blocked until changes to this segment are made.
                  setAllValidationMessages({
                    ...allValidationMessages,
                    [`conditions-tmp-${segmentCounter}`]: '',
                    [`id-tmp-${segmentCounter}`]: '',
                  });

                  setTimeout(() => {
                    // Trigger click on the newly created tab.
                    // Note: this is used to select the new segment which triggers it to appear in the
                    // priority list due to handleFormUpdate().
                    const tabs = Array.from(
                      document.querySelectorAll(`#segments-list-tabs .sds-tabs-item`),
                    ) as Array<HTMLElement>;
                    if (tabs) {
                      tabs.pop(); // This is the 'Add segment' button, remove it.
                      const lastTab = tabs.pop() as unknown as HTMLElement;
                      if (lastTab) {
                        (lastTab.querySelector(`button`) as HTMLButtonElement).click();
                      }
                    }
                  }, 50);
                }}
                text={labels.addSegment}
              />
            }
            tabList={tabs}
          />
        )}
        {!multiple && (
          <SegmentBuilderForm
            data={formatFromAPI(
              (find(segments, (segment): boolean => segment.internalId === segmentId) as EntitySegment) ||
                JSON.parse(defaultSegmentDataFromAPI),
            )}
            errors={currentValidationMessages}
            events={events}
            internalId={existingSegment?.internalId || `tmp-1`}
            onChange={(changedSegment, responses): void => {
              // Avoid race condition with a timeout on initial update.
              setTimeout((): void => {
                handleFormUpdate(changedSegment, responses, allValidationMessages);
              }, 10);
            }}
            onDelete={(): void => {}}
            schema={schema}
          />
        )}
        <hr className="segment-divider" />
        <div className="segment-actions">
          <ButtonList {...builderFormButtonListProps} />
        </div>
      </div>
    </DxpScreen>
  );
};
